import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import axiosBase, { AxiosResponse } from 'axios';
import { countersActions } from 'state/counters.slice';
import { buyerActions } from 'state/buyer.slice';
import { getAuctionsWatch } from './helpers';
import { REQUESTS } from './requests';

export const axios = axiosBase.create({
  baseURL: REQUESTS.baseURL,
  withCredentials: true,
});

const fetchAuctionsWatch = createAsyncThunk(
  'auctionsWatch/fetchAuctionsWatch',
  async (_, { signal, dispatch }) => {
    const { data }: AxiosResponse<AuctionsWatchTypes.AuctionsWatch> = await axios.get(
      REQUESTS.request.fetchAuctionsWatch,
      { signal },
    );

    const watchingAuctions = getAuctionsWatch(data.watchingAuctions);

    const count = data.watchingAuctions.length;

    dispatch(
      countersActions.updateCount({
        type: 'following',
        count,
      }),
    );

    return {
      watchingAuctions,
    };
  },
);

const setAuctionAsWatching = createAsyncThunk(
  'auctionsWatch/setAuctionAsWatching',
  async (
    { auction }: { auction: { auction_id?: number; watch?: boolean } },
    { getState, signal, dispatch },
  ) => {
    const state = getState() as RootState;

    if (!auction.auction_id) {
      return {
        watchingAuctions: state.auctionsWatch.auctions,
      };
    }

    const { data }: AxiosResponse<AuctionsWatchTypes.AuctionsWatch> = await axios.post(
      REQUESTS.request.setAuctionWatch,
      {
        auctionId: auction.auction_id,
        watch: auction.watch,
      },
      { signal },
    );

    const watchingAuctions = getAuctionsWatch(data.watchingAuctions);

    const count = data.watchingAuctions.length;

    dispatch(
      countersActions.updateCount({
        type: 'following',
        count,
      }),
    );

    return {
      watchingAuctions,
    };
  },
);

export const initialState: AuctionsWatchTypes.AuctionsWatchStateType = {
  auctions: {},
  pending: {
    watch: undefined,
    auctionId: undefined,
    auctionNumber: undefined,
    index: undefined,
    show: false,
  },
  isLoading: false,
};

export const auctionsWatchSlice = createSlice({
  name: 'auctionsWatch',
  initialState,
  reducers: {
    updateAuctions: (
      state,
      action: PayloadAction<{
        auctionId?: number;
        watch?: boolean;
      }>,
    ) => {
      if (!action.payload?.auctionId) {
        return;
      }

      if (
        typeof state.auctions[action.payload.auctionId] !== 'undefined' &&
        !action.payload.watch
      ) {
        delete state.auctions[action.payload.auctionId];
      }

      state.auctions = { ...state.auctions, [action.payload.auctionId]: action.payload.watch };
    },
    showPending: (
      state,
      action: PayloadAction<{
        auction: AuctionsTypes.AuctionType;
        index?: number;
      }>,
    ) => {
      state.pending = {
        auctionId: action.payload.auction.auction_id,
        auctionNumber: action.payload.auction.auction_number,
        watch: action.payload.auction.watch as boolean,
        index: action.payload.index,
        show: true,
      };
    },
    undoPending: (state) => {
      return {
        ...state,
        pending: {
          ...state.pending,
          watch: !state.pending.watch,
        },
      };
    },
    closePending: (state) => {
      state.pending.show = false;
    },
    clearPending: (state) => {
      state.pending = {
        auctionId: undefined,
        auctionNumber: undefined,
        watch: undefined,
        index: undefined,
        show: false,
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchAuctionsWatch.pending, (state, action) => {
      return {
        ...state,
        isLoading: true,
      };
    });
    builder.addCase(fetchAuctionsWatch.fulfilled, (state, action) => {
      return {
        ...state,
        auctions: action.payload.watchingAuctions,
        isLoading: false,
      };
    });
    builder.addCase(fetchAuctionsWatch.rejected, (state) => {
      return {
        ...state,
        isLoading: false,
      };
    });
    builder.addCase(setAuctionAsWatching.fulfilled, (state, action) => {
      return {
        ...state,
        auctions: action.payload.watchingAuctions,
        pending: {
          ...state.pending,
          show: false,
        },
      };
    });
    builder.addCase(setAuctionAsWatching.rejected, (state) => {
      return {
        ...state,
      };
    });
    builder.addCase(buyerActions.fetchPersonalizedAuctions.fulfilled, (state, action) => {
      return {
        ...state,
        auctions: getAuctionsWatch(action.payload.watchingAuctions),
      };
    });
    builder.addCase(buyerActions.updatePersonalizedAuctions.fulfilled, (state, action) => {
      if (!action.payload.watchingAuctions) {
        return state;
      }

      return {
        ...state,
        auctions: getAuctionsWatch(action.payload.watchingAuctions),
      };
    });
  },
});

export const auctionsWatchActions = {
  ...auctionsWatchSlice.actions,
  fetchAuctionsWatch,
  setAuctionAsWatching,
};

export default auctionsWatchSlice.reducer;
